import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Upload,
  Typography,
  Space,
  Row,
  Col,
  message,
  Spin,
  Tag,
  Input,
} from "antd";
import {
  InboxOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  SwapOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { oneClickCompare } from "../../../../Slices/policyCheckSlice";

const { Text, Title } = Typography;
const { Dragger } = Upload;

/**
 * One-Click Policy Comparison
 *
 * Upload old + new PDFs → click one button → AI does everything:
 * 1. Uploads both to S3
 * 2. AI extracts data from both
 * 3. AI compares old vs new
 * 4. Creates completed checklist with all discrepancies flagged
 */
const OneClickCompare = ({ requestId, onComplete }) => {
  const dispatch = useDispatch();
  const { oneClickStatus, priorComparisonSummary, priorAISummary } = useSelector(
    (state) => state.policyCheck
  );

  const [oldFile, setOldFile] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [insuredName, setInsuredName] = useState("");
  const [client, setClient] = useState("");

  const handleRun = async () => {
    if (!oldFile && !newFile) {
      message.warning("Upload at least one policy PDF");
      return;
    }

    try {
      const result = await dispatch(
        oneClickCompare({
          requestId,
          oldPolicyFile: oldFile,
          newPolicyFile: newFile,
          insuredName,
          client,
        })
      ).unwrap();

      const s = result.comparisonSummary;
      if (s) {
        message.success(
          `AI comparison complete! ${s.increased || 0} increased, ${s.decreased || 0} decreased, ${s.added || 0} added, ${s.removed || 0} removed`
        );
      } else {
        message.success("AI extraction complete! Checklist created.");
      }

      if (onComplete) onComplete(result);
    } catch (err) {
      message.error(err || "AI analysis failed");
    }
  };

  const isRunning = oneClickStatus === "uploading";

  const uploadProps = (side) => ({
    maxCount: 1,
    accept: ".pdf",
    beforeUpload: (file) => {
      if (file.type !== "application/pdf") {
        message.error("Only PDF files are accepted");
        return Upload.LIST_IGNORE;
      }
      if (side === "old") setOldFile(file);
      else setNewFile(file);
      return false; // prevent auto-upload
    },
    onRemove: () => {
      if (side === "old") setOldFile(null);
      else setNewFile(null);
    },
    fileList: side === "old" ? (oldFile ? [oldFile] : []) : (newFile ? [newFile] : []),
  });

  return (
    <div>
      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <ThunderboltOutlined style={{ color: "#7c3aed" }} />
            <span>AI Policy Comparison</span>
            <Tag
              style={{
                background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                color: "#fff",
                border: "none",
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              ONE-CLICK
            </Tag>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Upload old and new policy PDFs. AI will extract every coverage field,
          compare them, and flag all discrepancies automatically.
        </Text>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          {/* OLD POLICY */}
          <Col span={12}>
            <Card
              size="small"
              style={{
                borderColor: oldFile ? "#d97706" : "#d9d9d9",
                borderStyle: oldFile ? "solid" : "dashed",
                background: oldFile ? "#fffbeb" : "#fafafa",
              }}
            >
              <Tag color="warning" style={{ marginBottom: 8, fontWeight: 700 }}>
                OLD / EXPIRING POLICY
              </Tag>
              <Dragger
                {...uploadProps("old")}
                style={{ padding: "12px 0", background: "transparent", border: "none" }}
                disabled={isRunning}
              >
                <p style={{ marginBottom: 4 }}>
                  <InboxOutlined style={{ fontSize: 28, color: "#d97706" }} />
                </p>
                <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                  {oldFile ? oldFile.name : "Drop expiring policy PDF here"}
                </p>
              </Dragger>
            </Card>
          </Col>

          {/* NEW POLICY */}
          <Col span={12}>
            <Card
              size="small"
              style={{
                borderColor: newFile ? "#2563eb" : "#d9d9d9",
                borderStyle: newFile ? "solid" : "dashed",
                background: newFile ? "#eff6ff" : "#fafafa",
              }}
            >
              <Tag color="blue" style={{ marginBottom: 8, fontWeight: 700 }}>
                NEW / RENEWAL POLICY
              </Tag>
              <Dragger
                {...uploadProps("new")}
                style={{ padding: "12px 0", background: "transparent", border: "none" }}
                disabled={isRunning}
              >
                <p style={{ marginBottom: 4 }}>
                  <InboxOutlined style={{ fontSize: 28, color: "#2563eb" }} />
                </p>
                <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                  {newFile ? newFile.name : "Drop renewal policy PDF here"}
                </p>
              </Dragger>
            </Card>
          </Col>
        </Row>

        {/* Optional header fields */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text style={{ fontSize: 11, color: "#999" }}>Insured Name (optional — AI will auto-detect)</Text>
            <Input
              size="small"
              value={insuredName}
              onChange={(e) => setInsuredName(e.target.value)}
              placeholder="Auto-filled by AI"
              disabled={isRunning}
            />
          </Col>
          <Col span={12}>
            <Text style={{ fontSize: 11, color: "#999" }}>Client / Agency (optional)</Text>
            <Input
              size="small"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="Optional"
              disabled={isRunning}
            />
          </Col>
        </Row>

        {/* RUN BUTTON */}
        <Button
          type="primary"
          size="large"
          icon={isRunning ? <Spin size="small" /> : <ThunderboltOutlined />}
          onClick={handleRun}
          loading={isRunning}
          disabled={(!oldFile && !newFile) || isRunning}
          style={{
            width: "100%",
            height: 48,
            fontSize: 15,
            fontWeight: 700,
            background: isRunning
              ? "#d9d9d9"
              : "linear-gradient(135deg, #7c3aed, #2563eb)",
            border: "none",
          }}
        >
          {isRunning
            ? "AI is analyzing your policies..."
            : oldFile && newFile
            ? "Run AI Analysis — Compare Old vs New Policy"
            : oldFile
            ? "Run AI Analysis — Extract Old Policy Data"
            : newFile
            ? "Run AI Analysis — Extract New Policy Data"
            : "Upload at least one policy PDF"}
        </Button>

        {isRunning && (
          <div
            style={{
              marginTop: 12,
              padding: "12px 16px",
              background: "linear-gradient(135deg, #f5f3ff, #eff6ff)",
              border: "1px solid #c4b5fd",
              borderRadius: 8,
            }}
          >
            <Space>
              <Spin size="small" />
              <Text style={{ color: "#7c3aed", fontWeight: 600 }}>
                {oldFile && newFile
                  ? "Step 1: Extracting old policy → Step 2: Extracting new policy → Step 3: Comparing & flagging discrepancies..."
                  : "Extracting policy data and building checklist..."}
              </Text>
            </Space>
            <br />
            <Text style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
              This may take 30-60 seconds for large documents.
            </Text>
          </div>
        )}

        {/* Status hint */}
        {!isRunning && !oldFile && !newFile && (
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 8, fontSize: 11, fontStyle: "italic", textAlign: "center" }}
          >
            Upload both policies for full comparison, or a single policy for extraction only
          </Text>
        )}
      </Card>

      {/* AI SUMMARY RESULTS (shown after completion) */}
      {oneClickStatus === "done" && priorComparisonSummary && (
        <Card
          size="small"
          style={{ background: "#f0f5ff", borderColor: "#91d5ff", marginBottom: 16 }}
        >
          <Row gutter={16} align="middle">
            <Col>
              <CheckCircleOutlined style={{ fontSize: 20, color: "#52c41a" }} />
            </Col>
            <Col>
              <Text strong>AI Comparison Complete</Text>
            </Col>
            <Col>
              <Tag color="green">{priorComparisonSummary.no_change || 0} No Change</Tag>
              <Tag color="red">{priorComparisonSummary.increased || 0} Increased</Tag>
              <Tag color="blue">{priorComparisonSummary.decreased || 0} Decreased</Tag>
              <Tag color="purple">{priorComparisonSummary.added || 0} Added</Tag>
              <Tag color="volcano">{priorComparisonSummary.removed || 0} Removed</Tag>
              <Tag color="orange">{priorComparisonSummary.modified || 0} Modified</Tag>
            </Col>
            {priorComparisonSummary.criticalCount > 0 && (
              <Col>
                <Tag color="red">⚠ {priorComparisonSummary.criticalCount} Critical</Tag>
              </Col>
            )}
          </Row>
          {priorAISummary && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 12px",
                background: "#f5f3ff",
                borderRadius: 6,
                border: "1px solid #c4b5fd",
              }}
            >
              <Text style={{ fontSize: 12, color: "#7c3aed" }}>
                <strong>AI Summary:</strong> {priorAISummary}
              </Text>
            </div>
          )}
        </Card>
      )}

      {oneClickStatus === "error" && (
        <Card size="small" style={{ background: "#fff2f0", borderColor: "#ffa39e", marginBottom: 16 }}>
          <Text type="danger">AI analysis failed. Check the console for details and try again.</Text>
        </Card>
      )}
    </div>
  );
};

export default OneClickCompare;
